import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { UserStateEntry, UserStateMap, UserStateItemKey } from "../../types/user-state";
import { isUserStateItemKey } from "../../types/user-state";
import { normalizeUserStateVersion } from "../../utils/user-state/normalize-user-state-version";
import { buildUserStateItemApiPath } from "../../utils/user-state/build-user-state-item-api-path";
import { setEntry, replaceAll } from "../features/userState/userStateSlice";
import { handleUserStateDeleteLifecycle, handleUserStateUpdateLifecycle } from "./userStateOptimistic";
import { openSignInPrompt } from "../features/authPrompt/authPromptSlice";
import { hydrateJobsFromSSR } from "../features/jobs/jobsSlice";

export interface UpdateUserStateMutationArgs {
  itemKey: UserStateItemKey;
  data: Record<string, unknown>;
  version: number;
}

export interface DeleteUserStateMutationArgs {
  itemKey: UserStateItemKey;
  version?: number;
}

/**
 * Helper function to check if a userState map is effectively empty.
 * A map is considered empty if it has no entries or all entries are default entries (version 0, updatedAt null).
 */
function isUserStateEmpty(userState: UserStateMap): boolean {
  const entries = Object.keys(userState);
  if (entries.length === 0) {
    return true;
  }
  // Check if all entries are default entries (not actually saved to remote)
  return entries.every((key) => {
    const entry = userState[key];
    return entry.version === 0 && entry.updatedAt === null;
  });
}

/**
 * Helper function to sync all local userState entries to remote.
 * This is used when a user creates an account and has local state but no remote state.
 */
async function syncLocalUserStateToRemote(
  localEntries: UserStateMap,
  updateUserStateItem: (args: UpdateUserStateMutationArgs) => Promise<UserStateEntry>
): Promise<UserStateMap> {
  const syncedEntries: UserStateMap = {};
  const entriesToSync = Object.entries(localEntries).filter(([itemKey, entry]) => {
    if (!isUserStateItemKey(itemKey)) {
      return false;
    }
    // Only sync entries that have actual data (not default empty entries)
    return Boolean(entry.data && Object.keys(entry.data).length > 0);
  });

  console.log(`[user-state] Syncing ${entriesToSync.length} local entries to remote`);

  // Upload all entries in parallel, but handle errors gracefully
  const syncPromises = entriesToSync.map(async ([itemKey, entry]) => {
    try {
      // Use version 0 to create new entries on the server
      const result = await updateUserStateItem({
        itemKey: itemKey as UserStateItemKey,
        data: entry.data,
        version: 0,
      });
      syncedEntries[itemKey] = result;
      return { itemKey, success: true, result };
    } catch (error) {
      console.error(`[user-state] Failed to sync local entry ${itemKey} to remote:`, error);
      // If sync fails, keep the local entry
      syncedEntries[itemKey] = entry;
      return { itemKey, success: false, error };
    }
  });

  await Promise.all(syncPromises);

  const successCount = entriesToSync.filter(([itemKey]) => 
    syncedEntries[itemKey] && syncedEntries[itemKey].updatedAt !== null
  ).length;

  console.log(`[user-state] Successfully synced ${successCount}/${entriesToSync.length} local entries to remote`);

  return syncedEntries;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/api/user-state",
  credentials: "include",
});

export const userStateApi = createApi({
  reducerPath: "userStateApi",
  baseQuery: async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
      api.dispatch(openSignInPrompt());
    }
    return result;
  },
  tagTypes: ["UserState"],
  endpoints: (builder) => ({
    getUserState: builder.query<UserStateMap, void>({
      query: () => ({
        url: "",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...Object.keys(result).map((itemKey) => ({ type: "UserState" as const, id: itemKey })),
              { type: "UserState" as const, id: "LIST" },
            ]
          : [{ type: "UserState" as const, id: "LIST" }],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          // Get local state before fetching remote state
          const stateBeforeFetch = getState() as any;
          const localUserState = stateBeforeFetch.userState?.entries || {};
          const hasLocalEntries = Object.keys(localUserState).length > 0 && 
            !isUserStateEmpty(localUserState);

          const { data } = await queryFulfilled;
          
          // Check if remote state is empty (new account with no remote state)
          const isRemoteEmpty = isUserStateEmpty(data);
          
          // If remote is empty and local has entries, sync local to remote
          let finalUserState = data;
          if (isRemoteEmpty && hasLocalEntries) {
            console.log('[userStateApi] Remote state is empty but local has entries, syncing local to remote');
            
            // Create a temporary update function using the mutation
            const updateFn = async (args: UpdateUserStateMutationArgs): Promise<UserStateEntry> => {
              const result = await dispatch(
                userStateApi.endpoints.updateUserStateItem.initiate(args)
              ).unwrap();
              return result;
            };
            
            // Sync all local entries to remote
            const syncedEntries = await syncLocalUserStateToRemote(localUserState, updateFn);
            
            // Merge synced entries with any entries that might have been created during sync
            finalUserState = { ...syncedEntries };
            
            console.log('[userStateApi] Local state synced to remote, using synced state');
          }
          
          // Analyze fetched userState before updating Redux
          const jobKeys = Object.keys(finalUserState).filter(k => 
            k.startsWith('savedJob#') || k.startsWith('viewedJob#') || k.startsWith('appliedJob#')
          );
          
          const fetchedAnalysis = {
            saved: 0,
            viewed: 0,
            applied: 0,
            hidden: 0,
            interviewing: 0,
            offer: 0,
            rejected: 0,
            hired: 0,
            appliedNoState: 0,
          };
          
          jobKeys.forEach(key => {
            const entry = finalUserState[key];
            const entryData = entry?.data as Record<string, unknown> | undefined;
            if (!entryData) return;
            
            if (key.startsWith('savedJob#')) fetchedAnalysis.saved++;
            if (key.startsWith('viewedJob#')) fetchedAnalysis.viewed++;
            if (key.startsWith('appliedJob#')) {
              fetchedAnalysis.applied++;
              // applicationState is stored as a field in appliedJob# entries
              if (!entryData.applicationState) fetchedAnalysis.appliedNoState++;
              if (entryData.applicationState === 'interviewing') fetchedAnalysis.interviewing++;
              if (entryData.applicationState === 'offer') fetchedAnalysis.offer++;
              if (entryData.applicationState === 'rejected') fetchedAnalysis.rejected++;
              if (entryData.applicationState === 'hired') fetchedAnalysis.hired++;
            }
            
            if (entryData.isHidden === true) fetchedAnalysis.hidden++;
          });
          
          console.log('[userStateApi] getUserState fetched:', {
            totalEntries: Object.keys(finalUserState).length,
            jobEntries: jobKeys.length,
            wasRemoteEmpty: isRemoteEmpty,
            hadLocalEntries: hasLocalEntries,
            syncedLocalToRemote: isRemoteEmpty && hasLocalEntries,
            breakdown: {
              saved: fetchedAnalysis.saved,
              viewed: fetchedAnalysis.viewed,
              applied: fetchedAnalysis.applied,
              hidden: fetchedAnalysis.hidden,
              applicationStates: {
                interviewing: fetchedAnalysis.interviewing,
                offer: fetchedAnalysis.offer,
                rejected: fetchedAnalysis.rejected,
                hired: fetchedAnalysis.hired,
                none: fetchedAnalysis.appliedNoState,
              },
            },
          });
          
          dispatch(replaceAll(finalUserState));
          // Hydrate jobs from userState so saved/viewed/applied states are available
          dispatch(hydrateJobsFromSSR(finalUserState));
          
          // Log Redux state after update
          const state = getState() as any;
          const jobsState = state.jobs;
          const userStateEntries = state.userState?.entries || {};
          const allJobs = [...jobsState.savedJobs, ...jobsState.viewedJobs, ...jobsState.appliedJobs];
          
          const jobsAnalysis = {
            hidden: allJobs.filter((j: any) => j.isHidden === true).length,
            interviewing: allJobs.filter((j: any) => j.applicationState === 'interviewing').length,
            offer: allJobs.filter((j: any) => j.applicationState === 'offer').length,
            rejected: allJobs.filter((j: any) => j.applicationState === 'rejected').length,
            hired: allJobs.filter((j: any) => j.applicationState === 'hired').length,
            appliedNoState: allJobs.filter((j: any) => !j.applicationState).length,
          };
          
          console.log('[userStateApi] getUserState after Redux update:', {
            reduxUserStateEntries: Object.keys(userStateEntries).length,
            reduxJobs: {
              saved: jobsState.savedJobs.length,
              viewed: jobsState.viewedJobs.length,
              applied: jobsState.appliedJobs.length,
              total: allJobs.length,
            },
            reduxJobsAnalysis: {
              hidden: jobsAnalysis.hidden,
              applicationStates: {
                interviewing: jobsAnalysis.interviewing,
                offer: jobsAnalysis.offer,
                rejected: jobsAnalysis.rejected,
                hired: jobsAnalysis.hired,
                none: jobsAnalysis.appliedNoState,
              },
            },
          });
        } catch {
          // ignore failures here; handled by callers
        }
      },
    }),
    getUserStateItem: builder.query<UserStateEntry, UserStateItemKey>({
      query: (itemKey) => ({
        url: buildUserStateItemApiPath(itemKey),
        method: "GET",
      }),
      providesTags: (_result, _err, arg) => [{ type: "UserState", id: arg }],
      async onQueryStarted(itemKey, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setEntry({ itemKey, entry: data }));
        } catch {
          // ignore
        }
      },
    }),
    updateUserStateItem: builder.mutation<UserStateEntry, UpdateUserStateMutationArgs>({
      query: ({ itemKey, data, version }) => ({
        url: buildUserStateItemApiPath(itemKey),
        method: "PUT",
        body: {
          data,
          version: normalizeUserStateVersion(version),
        },
      }),
      invalidatesTags: (_result, _err, arg) => [{ type: "UserState", id: arg.itemKey }],
      async onQueryStarted(args, api) {
        await handleUserStateUpdateLifecycle(args, {
          dispatch: api.dispatch,
          getState: api.getState,
          queryFulfilled: api.queryFulfilled,
          upsertAll: (entries) =>
            api.dispatch(userStateApi.util.upsertQueryData("getUserState", undefined, entries)),
          upsertItem: (itemKey, entry) =>
            api.dispatch(userStateApi.util.upsertQueryData("getUserStateItem", itemKey, entry)),
        });
      },
    }),
    deleteUserStateItem: builder.mutation<void, DeleteUserStateMutationArgs>({
      query: ({ itemKey, version }) => {
        const v = normalizeUserStateVersion(version);
        return {
          url: `${buildUserStateItemApiPath(itemKey)}${v > 0 ? `?version=${v}` : ""}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_result, _err, arg) => [{ type: "UserState", id: arg.itemKey }],
      async onQueryStarted(args, api) {
        await handleUserStateDeleteLifecycle(args, {
          dispatch: api.dispatch,
          getState: api.getState,
          queryFulfilled: api.queryFulfilled,
          updateAll: (updater) => {
            try {
              return api.dispatch(userStateApi.util.updateQueryData("getUserState", undefined, updater));
            } catch {
              return undefined;
            }
          },
          updateItem: (itemKey, updater) => {
            try {
              return api.dispatch(userStateApi.util.updateQueryData("getUserStateItem", itemKey, updater));
            } catch {
              return undefined;
            }
          },
        });
      },
    }),
  }),
});

export const {
  useGetUserStateQuery,
  useGetUserStateItemQuery,
  useUpdateUserStateItemMutation,
  useDeleteUserStateItemMutation,
  util: userStateApiUtil,
} = userStateApi;

